import { MutationCache, QueryClient, QueryClientConfig, QueryCache } from "@tanstack/react-query";
import { isAuthCancellation, RATE_LIMIT_CODE, toApiError } from "./api-error";
import { presentableMessage } from "./presentable-message";

export const DEFAULT_STALE_TIME = 30_000;

export const MAX_QUERY_RETRIES = 2;

export function shouldRetryQuery(failureCount: number, error: unknown) {
  const apiError = toApiError(error);
  // Retrying a throttled request only deepens the throttling.
  if (apiError.code === RATE_LIMIT_CODE) return false;
  return apiError.isRetryable && failureCount < MAX_QUERY_RETRIES;
}

export type ErrorNotifier = (message: string) => void;

let errorNotifier: ErrorNotifier | null = null;

/** QueryProvider registers the Ant Design App message API so the default handler can reach the screen. */
export function setErrorNotifier(notifier: ErrorNotifier | null) {
  errorNotifier = notifier;
}

/** Opt-out for a call site rendering the failure itself, e.g. an inline error state. */
export interface RequestMeta extends Record<string, unknown> {
  suppressErrorMessage?: boolean;
}

function isSuppressed(meta: RequestMeta | undefined) {
  return meta?.suppressErrorMessage === true;
}

/**
 * Marks an imperative `fetchQuery` as presenting its own failure, so the default handler stays quiet
 * instead of adding a second message next to the local error state.
 */
export function withSuppressedErrorMessage<T extends { meta?: RequestMeta }>(options: T): T {
  return { ...options, meta: { ...options.meta, suppressErrorMessage: true } };
}

export function notifyRequestError(error: unknown, meta?: RequestMeta) {
  // Session teardown redirects to login and cancels its siblings; a toast would only add noise.
  if (isSuppressed(meta) || isAuthCancellation(error)) return;
  errorNotifier?.(presentableMessage(error));
}

export function createQueryClient({ defaultOptions, ...config }: QueryClientConfig = {}) {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => notifyRequestError(error, query.meta)
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _onMutateResult, mutation) => {
        notifyRequestError(error, mutation.meta);
      }
    }),
    ...config,
    defaultOptions: {
      ...defaultOptions,
      queries: {
        staleTime: DEFAULT_STALE_TIME,
        retry: shouldRetryQuery,
        ...defaultOptions?.queries
      },
      mutations: {
        retry: false,
        ...defaultOptions?.mutations
      }
    }
  });
}

export const appQueryClient = createQueryClient();
